import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronLeft, ChevronRight, Filter, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { addDays, today } from '@mkcore/shared';
import { bulkDeleteEntries, bulkUpdateEntries, deleteEntry, restoreEntry, startTimer, updateEntry } from '@mkcore/time/schema';
import { Badge, BulkBar, Button, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, SelectAllCheckbox, SelectRowCheckbox, Skeleton, Switch, cn, toast, useSelection } from '@mkcore/ui';
import { formatDate, formatDuration } from '@/lib/format';
import { useActionMutation, useErrorMessage, UNDO_TOAST_MS } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { EntryDialog, type TimeEntry } from '@/features/time/entry-dialog';
import { EditorActivity } from '@/features/time/editor-activity';
import { PresenceVisits } from '@/features/time/presence-visits';
import { Dot, ProjectSelect, WorkTypeSelect } from '@/features/time/pickers';
import { entriesQuery, summaryQuery } from '@/features/time/queries';
import { timeOf } from '@/features/time/util';

const search = z.object({
  date: z.string().optional(),
  q: z.string().optional(),
  projectId: z.string().optional(),
  tag: z.string().optional(),
  billable: z.boolean().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  entry: z.string().optional(),
});

export const Route = createFileRoute('/_app/time/')({ validateSearch: search, component: TimeDay });

/** Description that edits in place: click, type, Enter saves, Escape cancels (CO-0204). */
function InlineText({ value, placeholder, disabled, onSave }: { value: string; placeholder: string; disabled?: boolean; onSave: (v: string) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (draft === null || !ref.current || document.activeElement === ref.current) return;
    ref.current.focus();
    ref.current.setSelectionRange(ref.current.value.length, ref.current.value.length);
  }, [draft]);
  const commit = () => {
    if (draft !== null && draft.trim() !== value) onSave(draft.trim());
    setDraft(null);
  };
  if (draft === null)
    return (
      <button type="button" disabled={disabled} className={cn('block w-full truncate rounded-sm text-left hover:bg-sunken', !value && 'text-fg-3')} onClick={() => setDraft(value)} title={value}>
        {value || placeholder}
      </button>
    );
  return (
    <input
      ref={ref}
      className="w-full rounded-sm border border-line bg-surface px-1 text-sm"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setDraft(null);
      }}
    />
  );
}

/** The day overview with inline editing, the filters and search over a period (CO-0204, CO-0206, CO-0211, CO-0218). */
function TimeDay() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canCreate = useCan('time:create');
  const date = s.date ?? today();
  const filtering = !!(s.q || s.projectId || s.tag || s.billable !== undefined || s.from || s.to);
  const [showFilters, setShowFilters] = useState(filtering);
  const range = filtering ? { from: s.from ?? addDays(today(), -30), to: s.to ?? today() } : { from: date, to: date };
  const filters = { ...range, q: s.q || undefined, projectId: s.projectId || undefined, tag: s.tag || undefined, billable: s.billable, limit: 100 };
  const q = useInfiniteQuery(entriesQuery(filters));
  const summary = useQuery(summaryQuery(range));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const [dialog, setDialog] = useState<{ open: boolean; entry: TimeEntry | null }>({ open: false, entry: null });
  const invalidate = [['time']];
  const update = useActionMutation(updateEntry, { invalidate, silentErrors: true });
  const restore = useActionMutation(restoreEntry, { invalidate, success: t('time.restored') });
  const remove = useActionMutation(deleteEntry, {
    invalidate,
    silentErrors: true,
    onSuccess: (e) => toast(t('time.deleted'), { duration: UNDO_TOAST_MS, action: { label: t('common.restore'), onClick: () => restore.mutate({ input: { id: e.id } }) } }),
  });
  const cont = useActionMutation(startTimer, { invalidate, success: t('time.timer.startedToast') });
  const selectable = rows.filter((e) => !e.locked).map((e) => e.id);
  const selection = useSelection(selectable);
  const patch = (e: TimeEntry, input: Record<string, unknown>) =>
    update.mutate({ input: { id: e.id, ...input } as never, ifMatch: e.version }, { onError: (err) => toast.error(message(err)) });

  const groups = new Map<string, TimeEntry[]>();
  for (const r of rows) groups.set(r.date, [...(groups.get(r.date) ?? []), r]);
  const dayTotal = (d: string) => summary.data?.days.find((x) => x.date === d)?.seconds ?? groups.get(d)?.reduce((a, e) => a + e.durationSeconds, 0) ?? 0;

  return (
    <>
      <PageHeader
        title={t('time.title')}
        description={t('time.description')}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowFilters((v) => !v)} aria-pressed={showFilters}>
              <Filter /> {t('time.filters')}
            </Button>
            {canCreate && (
              <Button onClick={() => setDialog({ open: true, entry: null })}>
                <Plus /> {t('time.new')}
              </Button>
            )}
          </div>
        }
      />
      {!filtering && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon" aria-label={t('time.prevDay')} onClick={() => set({ date: addDays(date, -1) })}>
            <ChevronLeft />
          </Button>
          <Input type="date" className="w-44" aria-label={t('time.fields.date')} value={date} onChange={(e) => e.target.value && set({ date: e.target.value })} />
          <Button variant="ghost" size="icon" aria-label={t('time.nextDay')} onClick={() => set({ date: addDays(date, 1) })}>
            <ChevronRight />
          </Button>
          <Button variant="secondary" size="sm" onClick={() => set({ date: undefined })} disabled={date === today()}>
            <CalendarDays /> {t('time.today')}
          </Button>
          <div className="ml-auto text-sm text-fg-2">
            {t('time.dayTotal')}: <span className="font-bold text-fg-1">{formatDuration(summary.data?.seconds ?? 0)}</span>
            {summary.data?.goalSeconds ? <span className="text-fg-3"> {t('time.ofGoal', { goal: formatDuration(summary.data.goalSeconds) })}</span> : null}
          </div>
        </div>
      )}
      {showFilters && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-line-subtle bg-surface p-3">
          <Field label={t('common.search')} className="w-56">
            {(p) => <Input {...p} value={s.q ?? ''} placeholder={t('time.searchHint')} onChange={(e) => set({ q: e.target.value || undefined })} />}
          </Field>
          <Field label={t('time.fields.project')} className="w-56">{(p) => <ProjectSelect {...p} value={s.projectId ?? ''} emptyLabel={t('common.all')} onChange={(v) => set({ projectId: v || undefined })} />}</Field>
          <Field label={t('time.fields.tag')} className="w-36">{(p) => <Input {...p} value={s.tag ?? ''} onChange={(e) => set({ tag: e.target.value || undefined })} />}</Field>
          <Field label={t('time.fields.billable')} className="w-32">
            {(p) => (
              <NativeSelect {...p} value={s.billable === undefined ? '' : String(s.billable)} onChange={(e) => set({ billable: e.target.value === '' ? undefined : e.target.value === 'true' })}>
                <option value="">{t('common.all')}</option>
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
              </NativeSelect>
            )}
          </Field>
          <Field label={t('time.from')} className="w-40">{(p) => <Input {...p} type="date" value={s.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} />}</Field>
          <Field label={t('time.to')} className="w-40">{(p) => <Input {...p} type="date" value={s.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} />}</Field>
          {filtering && (
            <Button variant="ghost" size="sm" onClick={() => set({ q: undefined, projectId: undefined, tag: undefined, billable: undefined, from: undefined, to: undefined })}>
              {t('time.clearFilters')}
            </Button>
          )}
        </div>
      )}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-48" />
      ) : !rows.length ? (
        <EmptyState title={filtering ? t('time.noResults') : t('time.empty')} description={filtering ? undefined : t('time.emptyHint')} />
      ) : (
        <div className="grid gap-6">
          {[...groups.entries()].map(([d, entries]) => (
            <section key={d} aria-label={formatDate(d)}>
              {(filtering || groups.size > 1) && (
                <h2 className="mb-2 flex items-baseline gap-3 text-sm font-bold uppercase tracking-caps text-fg-2">
                  {formatDate(d)} <span className="font-normal normal-case tracking-normal text-fg-3">{formatDuration(dayTotal(d))}</span>
                </h2>
              )}
              <ul className="divide-y divide-line-subtle rounded-lg border border-line-subtle bg-surface">
                {entries.map((e) => (
                  <li
                    key={e.id}
                    className={cn(
                      'grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1 px-3 py-2 sm:grid-cols-[auto_minmax(0,1.2fr)_minmax(0,2fr)_auto_auto_auto]',
                      s.entry === e.id && 'ring-2 ring-gold',
                      selection.isSelected(e.id) && 'bg-gold/5',
                    )}
                  >
                    <div className="flex items-center">
                      {e.locked ? <span className="inline-block size-4" /> : <SelectRowCheckbox selection={selection} rowKey={e.id} label={t('common.selectRow', { name: e.description || e.projectName })} />}
                    </div>
                    <div className="flex min-w-0 items-center gap-2">
                      <Dot colour={e.projectColour} />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold">{e.projectName}</div>
                        <div className="truncate text-xs text-fg-3">
                          {e.organisationName ?? t('projects.internal')} · {e.workTypeName}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 flex min-w-0 items-center gap-2 text-sm sm:col-span-1">
                      <InlineText value={e.description} placeholder={t('time.fields.description')} disabled={e.locked} onSave={(v) => patch(e, { description: v })} />
                      {e.tags.map((tag) => (
                        <Badge key={tag} tone="neutral">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="text-right text-sm tabular-nums">
                      {e.timed && (
                        <div className="text-xs text-fg-3">
                          {timeOf(e.startedAt)}–{timeOf(e.endedAt)}
                        </div>
                      )}
                      <div className="font-bold">{formatDuration(e.durationSeconds)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch checked={e.billable} disabled={e.locked} aria-label={t('time.fields.billable')} onCheckedChange={(v) => patch(e, { billable: v })} />
                      <span className="hidden text-xs text-fg-3 md:inline">€</span>
                    </div>
                    <div className="flex items-center">
                      {canCreate && (
                        <Button size="icon" variant="ghost" aria-label={t('time.continue')} onClick={() => cont.mutate({ input: { fromEntryId: e.id } })}>
                          <Play />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" aria-label={t('time.edit')} onClick={() => setDialog({ open: true, entry: e })}>
                        <Pencil />
                      </Button>
                      {!e.locked && (
                        <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={() => remove.mutate({ input: { id: e.id } }, { onError: (err) => toast.error(message(err)) })}>
                          <Trash2 />
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {q.hasNextPage && (
            <div className="flex justify-center">
              <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                {t('common.more')}
              </Button>
            </div>
          )}
          <BulkActions selection={selection} />
        </div>
      )}
      {rows.length > 0 && !filtering && (
        <label className="mt-3 flex items-center gap-2 text-xs text-fg-3">
          <SelectAllCheckbox selection={selection} label={t('common.selectAll')} /> {t('time.bulk.selectAllHint')}
        </label>
      )}
      {!filtering && <PresenceVisits date={date} />}
      {!filtering && <EditorActivity date={date} canBook={canCreate} />}
      <EntryDialog open={dialog.open} entry={dialog.entry} init={{ date }} onClose={() => setDialog((d) => ({ ...d, open: false }))} />
    </>
  );
}

/** The actions on selected entries (CO-0213): project, work type, billable, a tag, a day, or delete. */
function BulkActions({ selection }: { selection: ReturnType<typeof useSelection<string>> }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [projectId, setProjectId] = useState('');
  const [workTypeId, setWorkTypeId] = useState('');
  const [tag, setTag] = useState('');
  const [date, setDate] = useState('');
  const ids = [...selection.selected];
  const done = (r: { updated?: number; deleted?: number; skipped: { id: string; reason: string }[] }) => {
    const n = r.updated ?? r.deleted ?? 0;
    toast.success(t('time.bulk.done', { count: n }));
    if (r.skipped.length) toast.warning(t('time.bulk.skipped', { count: r.skipped.length }));
    selection.clear();
  };
  const update = useActionMutation(bulkUpdateEntries, { invalidate: [['time']], silentErrors: true, onSuccess: done });
  const remove = useActionMutation(bulkDeleteEntries, { invalidate: [['time']], silentErrors: true, onSuccess: done });
  const apply = (set: Record<string, unknown>) => update.mutate({ input: { ids, set } as never }, { onError: (e) => toast.error(message(e)) });
  const busy = update.isPending || remove.isPending;
  return (
    <BulkBar count={selection.count} label={t('common.selected', { count: selection.count })} clearLabel={t('common.clearSelection')} onClear={selection.clear}>
      <ProjectSelect
        className="h-8 w-44 text-fg-1"
        value={projectId}
        emptyLabel={t('time.bulk.project')}
        onChange={(v) => {
          setProjectId(v);
          if (v) apply({ projectId: v });
        }}
      />
      <WorkTypeSelect
        className="h-8 w-36 text-fg-1"
        value={workTypeId}
        emptyLabel={t('time.bulk.workType')}
        onChange={(v) => {
          setWorkTypeId(v);
          if (v) apply({ workTypeId: v });
        }}
      />
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => apply({ billable: true })}>
        {t('time.bulk.billable')}
      </Button>
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => apply({ billable: false })}>
        {t('time.bulk.nonBillable')}
      </Button>
      <form
        className="flex items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (tag.trim()) apply({ addTags: [tag.trim()] });
          setTag('');
        }}
      >
        <Input className="h-8 w-28 text-fg-1" placeholder={t('time.bulk.tag')} aria-label={t('time.bulk.tag')} value={tag} onChange={(e) => setTag(e.target.value)} />
      </form>
      <Input
        type="date"
        className="h-8 w-40 text-fg-1"
        aria-label={t('time.bulk.move')}
        value={date}
        onChange={(e) => {
          setDate(e.target.value);
          if (e.target.value) apply({ date: e.target.value });
        }}
      />
      <Button size="sm" variant="danger" disabled={busy} onClick={() => remove.mutate({ input: { ids } }, { onError: (e) => toast.error(message(e)) })}>
        <Trash2 /> {t('common.delete')}
      </Button>
    </BulkBar>
  );
}
